import React, { useState, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  User,
  Calendar,
  Clock,
  ExternalLink,
  BarChart3,
  AlertTriangle,
  CheckSquare
} from 'lucide-react';
import { useCachedData } from '../hooks/useCachedData';
import { useStickyFilter } from '../hooks/useStickyFilter';
import PCTasksChecklist from './PCTasksChecklist';

const PCTasks = ({ currentUser }) => {
  const [selectedTab, setSelectedTab] = useState('fms');
  const [searchTerm, setSearchTerm] = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();
  const [groupBy, setGroupBy] = useState('fms');
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch PC FMS data with the correct API endpoint
  const fetchPCFMSData = useCallback(async () => {
    try {
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_FMS;
      const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_FMS;
      
      const apiUrl = '/api/sheets';
      const params = new URLSearchParams({
        sheetId: spreadsheetId,
        sheetName: sheetName,
        range: 'A1:M'
      });
      
      const response = await fetch(`${apiUrl}?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PC FMS data: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];

      if (rows.length === 0) {
        return [];
      }

      // Get header row
      const headers = rows[0];
      
      // Process data rows (skip header) and filter by current user's PC column
      const fmsTasks = rows.slice(1)
        .filter(row => row && row.length > 0) // Filter out empty rows
        .map((row, index) => {
          const task = {};
          
          // Map each column to its header
          headers.forEach((header, colIndex) => {
            if (header && row[colIndex] !== undefined) {
              const key = cleanHeaderName(header);
              task[key] = row[colIndex];
            }
          });

          // Add computed fields
          task.id = index + 1;
          task.rowNumber = index + 2; // Actual row number in sheet (1 for header + index)
          
          return task;
        })
        .filter(task => {
          // Filter tasks by current user's name in PC column
          const taskPC = getPCDeo(task);
          return taskPC === currentUser.name;
        });

      return fmsTasks;
    } catch (error) {
      console.error('Error fetching PC FMS data:', error);
      throw error;
    }
  }, [currentUser.name]);

  // Use cached data hook with force refresh option
  const { data: allTasks, loading, error, lastRefresh } = useCachedData(
    'pc', 
    currentUser, 
    fetchPCFMSData
  );

  // Add local loading state for manual refresh
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  // Enhanced refresh function that ensures fresh data from server
  const refresh = useCallback(async () => {
    console.log(`🔄 Force refreshing PC data for ${currentUser.name} - fetching fresh from server...`);
    
    setIsManualRefreshing(true);
    
    try {
      // Import dataManager to clear cache first
      const { default: dataManager } = await import('../utils/DataManager');
      
      // Clear the specific cache entry to force fresh fetch
      const cacheKey = `pc_${currentUser.name}`;
      dataManager.cache.delete(cacheKey);
      console.log(`🗑️ Cleared cache for ${cacheKey}`);
      
      // Directly call the fetch function to get fresh data from server
      const freshData = await fetchPCFMSData();
      
      // Update cache with fresh data
      dataManager.setData('pc', currentUser.name, freshData);
      
      console.log(`✅ Successfully refreshed PC data: ${freshData.length} tasks`);
      return freshData;
    } catch (error) {
      console.error('❌ Error during manual refresh:', error);
      throw error;
    } finally {
      setIsManualRefreshing(false);
    }
  }, [fetchPCFMSData, currentUser.name]);

  // Combined loading state
  const isRefreshing = loading || isManualRefreshing;

  const cleanHeaderName = (header) => {
    return header
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Helper functions (moved before useMemo to avoid initialization errors)
  const getTaskInfo = (task) => {
    return task.task_info || task.taskinfo || task.what_to_do || '';
  };

  const getWhatToDo = (task) => {
    return task.what_to_do || task.whattodo || '';
  };

  const getDoer = (task) => {
    return task.doer || task.assignedTo || '';
  };

  const getPCDeo = (task) => {
    return task.pc || task.pcdeo || task.pc_deo || '';
  };

  const getFMSType = (task) => {
    return task.fms || task.fms_type || 'Unknown';
  };

  const getDeadline = (task) => {
    return task.deadline || task.due_date || task.duedate || '';
  };

  const getDelay = (task) => {
    return task.delay || '';
  };

  const getLink = (task) => {
    return task.link || '';
  };

  // Check if task is overdue (has delay content) - UPDATED TO USE NUMERIC LOGIC
  const isTaskOverdue = (task) => {
    const delay = parseFloat(task.delay || 0);
    return delay > 0; // Any positive number means overdue
  };

  // Filter tasks - separate FMS tasks from Checklist tasks
  const { fmsTasks, checklistTasks } = useMemo(() => {
    if (!Array.isArray(allTasks)) {
      return { fmsTasks: [], checklistTasks: [] };
    }

    const fms = [];
    const checklist = [];

    allTasks.forEach(task => {
      const fmsType = getFMSType(task);
      if (fmsType.toLowerCase().includes('checklist task')) {
        checklist.push(task);
      } else {
        fms.push(task);
      }
    });

    return { fmsTasks: fms, checklistTasks: checklist };
  }, [allTasks]);

  // Use fmsTasks for the FMS tab (excluding checklist tasks)
  const tasks = selectedTab === 'fms' ? fmsTasks : allTasks;

  // Create hierarchical structure for AllTask view
  const getHierarchicalTasks = () => {
    let filteredTasks = tasks;

    // Filter by search term
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        getTaskInfo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getWhatToDo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getDoer(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getFMSType(task).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Group tasks hierarchically: FMS -> Doer -> Tasks
    const hierarchy = {};
    
    filteredTasks.forEach(task => {
      const fmsType = getFMSType(task);
      const doer = getDoer(task) || 'Unassigned';
      
      if (!hierarchy[fmsType]) {
        hierarchy[fmsType] = {};
      }
      
      if (!hierarchy[fmsType][doer]) {
        hierarchy[fmsType][doer] = [];
      }
      
      hierarchy[fmsType][doer].push(task);
    });

    // Sort FMS types alphabetically
    const sortedHierarchy = {};
    Object.keys(hierarchy).sort((a, b) => a.localeCompare(b)).forEach(fmsType => {
      sortedHierarchy[fmsType] = {};
      
      // Sort doers within each FMS type alphabetically
      Object.keys(hierarchy[fmsType]).sort((a, b) => a.localeCompare(b)).forEach(doer => {
        sortedHierarchy[fmsType][doer] = hierarchy[fmsType][doer];
      });
    });

    return sortedHierarchy;
  };

  // Group tasks based on selected groupBy
  const getGroupedTasks = () => {
    let filteredTasks = tasks;

    // Filter by search term
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        getTaskInfo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getWhatToDo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getDoer(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getFMSType(task).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Group tasks
    const grouped = {};
    
    if (groupBy === 'all') {
      return getHierarchicalTasks(); // Return hierarchical structure for AllTask
    } else if (groupBy === 'fms') {
      filteredTasks.forEach(task => {
        const fmsType = getFMSType(task);
        if (!grouped[fmsType]) {
          grouped[fmsType] = [];
        }
        grouped[fmsType].push(task);
      });
      
      // Sort FMS types alphabetically
      const sortedGrouped = {};
      Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach(fmsType => {
        sortedGrouped[fmsType] = grouped[fmsType];
      });
      return sortedGrouped;
    } else if (groupBy === 'doer') {
      filteredTasks.forEach(task => {
        const doer = getDoer(task) || 'Unassigned';
        if (!grouped[doer]) {
          grouped[doer] = [];
        }
        grouped[doer].push(task);
      });
      
      // Sort doers alphabetically
      const sortedGrouped = {};
      Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach(doer => {
        sortedGrouped[doer] = grouped[doer];
      });
      return sortedGrouped;
    }

    return grouped;
  };

  const groupedTasks = getGroupedTasks();

  // Helper function to extract delay in days
  const getDelayInDays = (task) => {
    const delay = getDelay(task);
    if (!delay || delay.trim() === '') return 0;
    
    // Try to extract number from delay text
    const match = delay.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Get summary statistics
  const getSummaryStats = () => {
    if (!Array.isArray(tasks)) return {};
    
    const totalTasks = tasks.length;
    const overdueTasks = tasks.filter(task => isTaskOverdue(task));
    
    // Calculate average delay
    const totalDelay = overdueTasks.reduce((sum, task) => sum + getDelayInDays(task), 0);
    const avgDelay = overdueTasks.length > 0 ? (totalDelay / overdueTasks.length).toFixed(1) : '0';

    // Calculate doer-wise and FMS-wise task distribution
    const doerTaskCount = {};
    const doerDelaySum = {};
    const fmsOverdueCount = {};
    
    tasks.forEach(task => {
      const doer = getDoer(task) || 'Unknown';
      const fmsType = getFMSType(task);
      
      // Count doer tasks
      doerTaskCount[doer] = (doerTaskCount[doer] || 0) + 1;
      
      if (isTaskOverdue(task)) {
        doerDelaySum[doer] = (doerDelaySum[doer] || 0) + getDelayInDays(task);
        fmsOverdueCount[fmsType] = (fmsOverdueCount[fmsType] || 0) + 1;
      }
    });

    // Top doers with overdue tasks
    const topDoersOverdue = Object.keys(doerTaskCount)
      .map(doer => {
        const totalTasks = doerTaskCount[doer];
        const totalDelay = doerDelaySum[doer] || 0;
        const overdueCount = tasks.filter(task => getDoer(task) === doer && isTaskOverdue(task)).length;
        
        return {
          name: doer,
          count: overdueCount,
          total: totalTasks,
          percentage: totalTasks > 0 ? ((overdueCount / totalTasks) * 100).toFixed(1) : 0,
          avgDelay: overdueCount > 0 ? (totalDelay / overdueCount).toFixed(1) : 0
        };
      })
      .filter(data => data.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Top FMS types with overdue tasks
    const topFMSOverdue = Object.keys(fmsOverdueCount)
      .map(fmsType => {
        const totalTasks = tasks.filter(task => getFMSType(task) === fmsType).length;
        const overdueCount = fmsOverdueCount[fmsType];
        const totalDelay = overdueTasks
          .filter(task => getFMSType(task) === fmsType)
          .reduce((sum, task) => sum + getDelayInDays(task), 0);
        
        return {
          name: fmsType,
          count: overdueCount,
          total: totalTasks,
          percentage: totalTasks > 0 ? ((overdueCount / totalTasks) * 100).toFixed(1) : 0,
          avgDelay: overdueCount > 0 ? (totalDelay / overdueCount).toFixed(1) : 0
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalTasks,
      totalOverdue: overdueTasks.length,
      overduePercentage: totalTasks > 0 ? ((overdueTasks.length / totalTasks) * 100).toFixed(1) : 0,
      avgDelayDays: avgDelay,
      topDoersOverdue,
      topFMSOverdue
    };
  };

  const summaryStats = getSummaryStats();

  const toggleGroupExpansion = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Render individual task item
  const renderTaskItem = (task, index) => {
    const isOverdue = isTaskOverdue(task);
    const link = getLink(task);
    return (
      <div key={index} className={`k-task-row ${isOverdue ? 'overdue' : 'low'}`}>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="k-badge-role text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: 'var(--green-dim)', color: 'var(--green-primary)' }}>#{task.rowNumber || index + 1}</span>
            {isOverdue && <span className="k-badge-overdue">Overdue</span>}
            <span className="k-task-title flex-1 min-w-0">{getTaskInfo(task) || getWhatToDo(task) || 'No task description'}</span>
          </div>
          <div className="k-task-meta">
            <span className="k-task-meta-item"><User size={11} /> {getDoer(task) || 'Unassigned'}</span>
            {getDeadline(task) && <span className="k-task-meta-item"><Calendar size={11} /> {getDeadline(task)}</span>}
            <span className="k-task-meta-item"><FileText size={11} /> {getFMSType(task)}</span>
            {isOverdue && getDelay(task) && (
              <span className="k-task-meta-item" style={{ color: 'var(--c-danger)' }}><Clock size={11} /> {getDelay(task)}</span>
            )}
          </div>
        </div>
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" title="Open task link"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: 'var(--gold-dim)', color: 'var(--gold-primary)', border: '1px solid rgba(200,146,42,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    );
  };

  // Render grouped view
  const renderRegularGroupedView = (grouped) => (
    Object.keys(grouped).map(groupName => {
      const groupTasks = grouped[groupName];
      const overdue = groupTasks.filter(t => isTaskOverdue(t)).length;
      const isOpen = expandedGroups[groupName];
      return (
        <div key={groupName} className="k-card overflow-hidden mb-2.5">
          <div
            onClick={() => toggleGroupExpansion(groupName)}
            className="flex items-center justify-between gap-3 px-4 py-3.5 cursor-pointer transition-colors hover:bg-surface-2"
            style={{ borderBottom: isOpen ? '1px solid var(--border)' : 'none' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="k-stat-icon w-9 h-9 rounded-xl flex-shrink-0" style={{ background: overdue > 0 ? 'var(--c-danger-bg)' : 'var(--green-dim)' }}>
                <FileText size={16} style={{ color: overdue > 0 ? 'var(--c-danger)' : 'var(--green-primary)' }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{groupName}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{groupTasks.length} tasks · {overdue} overdue</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {overdue > 0 && <span className="k-badge-overdue">{overdue} overdue</span>}
              <span className="k-badge-score">{groupTasks.length}</span>
              {isOpen ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />}
            </div>
          </div>
          {isOpen && (
            <div className="k-task-list">
              {groupTasks.map((task, i) => renderTaskItem(task, i))}
            </div>
          )}
        </div>
      );
    })
  );

  // Render hierarchical view
  const renderHierarchicalView = (hierarchy) => (
    Object.keys(hierarchy).map(fmsType => {
      const fmsKey = `fms-${fmsType}`;
      const fmsDoers = hierarchy[fmsType];
      const fmsTotalTasks = Object.values(fmsDoers).reduce((sum, t) => sum + t.length, 0);
      const fmsOverdue = Object.values(fmsDoers).reduce((sum, t) => sum + t.filter(task => isTaskOverdue(task)).length, 0);
      const isFmsOpen = expandedGroups[fmsKey];
      return (
        <div key={fmsKey} className="k-card overflow-hidden mb-2.5">
          <div
            onClick={() => toggleGroupExpansion(fmsKey)}
            className="flex items-center justify-between gap-3 px-4 py-3.5 cursor-pointer"
            style={{ borderBottom: isFmsOpen ? '1px solid var(--border)' : 'none' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="k-stat-icon w-9 h-9 rounded-xl flex-shrink-0" style={{ background: fmsOverdue > 0 ? 'var(--c-danger-bg)' : 'var(--green-dim)' }}>
                <FileText size={16} style={{ color: fmsOverdue > 0 ? 'var(--c-danger)' : 'var(--green-primary)' }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{fmsType}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmsTotalTasks} tasks · {fmsOverdue} overdue · {Object.keys(fmsDoers).length} doers</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {fmsOverdue > 0 && <span className="k-badge-overdue">{fmsOverdue}</span>}
              <span className="k-badge-score">{fmsTotalTasks}</span>
              {isFmsOpen ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />}
            </div>
          </div>
          {isFmsOpen && (
            <div style={{ borderLeft: '3px solid var(--green-light)', marginLeft: '16px' }}>
              {Object.keys(fmsDoers).map(doer => {
                const doerKey = `${fmsKey}-${doer}`;
                const doerTasks = fmsDoers[doer];
                const doerOverdue = doerTasks.filter(t => isTaskOverdue(t)).length;
                const isDoerOpen = expandedGroups[doerKey];
                return (
                  <div key={doerKey} style={{ borderBottom: '1px solid var(--border)' }}>
                    <div
                      onClick={() => toggleGroupExpansion(doerKey)}
                      className="flex items-center justify-between px-4 py-2.5 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <User size={13} style={{ color: 'var(--green-primary)', flexShrink: 0 }} />
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{doer}</span>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {doerTasks.length} task{doerTasks.length !== 1 ? 's' : ''}{doerOverdue > 0 ? ` · ${doerOverdue} overdue` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={doerOverdue > 0 ? 'k-badge-overdue' : 'k-badge-completed'}>{doerTasks.length}</span>
                        {isDoerOpen ? <ChevronUp size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} />}
                      </div>
                    </div>
                    {isDoerOpen && (
                      <div className="k-task-list" style={{ paddingLeft: '20px' }}>
                        {doerTasks.map((task, i) => renderTaskItem(task, i))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    })
  );

  if (loading) {
    return (
      <div className="k-card">
        <div className="k-loading">
          <div className="k-loading-spinner"></div>
          <p className="k-loading-text">Loading PC tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-card">
        <div className="k-error">
          <div className="k-error-icon"><AlertTriangle className="w-7 h-7" /></div>
          <p className="k-error-title">Error Loading PC Tasks</p>
          <p className="k-error-msg">{error}</p>
          <button onClick={refresh} className="k-btn-primary mt-2">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">

      {/* Tabs */}
      <div className="k-tabs-wrap">
        <div className="k-tabs-main px-4 pt-1">
          <button className={`k-tab-btn ${selectedTab === 'fms' ? 'active' : ''}`} onClick={() => setSelectedTab('fms')}>
            FMS <span className="k-tab-count">{fmsTasks.length}</span>
          </button>
          <button className={`k-tab-btn ${selectedTab === 'checklist' ? 'active' : ''}`} onClick={() => setSelectedTab('checklist')}>
            <CheckSquare size={13} style={{ marginRight: '4px' }} /> Checklist <span className="k-tab-count">{checklistTasks.length}</span>
          </button>
        </div>
      </div>

      {selectedTab === 'checklist' ? (
        <PCTasksChecklist tasks={checklistTasks} currentUser={currentUser} loading={loading} error={error} />
      ) : (
        <>
          {/* Summary stats */}
          <div className="k-stats-grid-4">
            {[
              { label: 'Total Tasks',  value: summaryStats.totalTasks,        icon: FileText,      color: 'var(--teal-primary)',  bg: 'var(--teal-dim)' },
              { label: 'Overdue',      value: summaryStats.totalOverdue,       icon: AlertTriangle, color: 'var(--c-danger)',      bg: 'var(--c-danger-bg)' },
              { label: 'Avg Delay',    value: `${summaryStats.avgDelayDays}d`, icon: Clock,         color: 'var(--c-warning)',     bg: 'var(--c-warning-bg)' },
              { label: 'Overdue %',    value: `${summaryStats.overduePercentage}%`, icon: BarChart3,
                color: summaryStats.overduePercentage > 30 ? 'var(--c-danger)' : 'var(--c-success)',
                bg:    summaryStats.overduePercentage > 30 ? 'var(--c-danger-bg)' : 'var(--c-success-bg)' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="k-stat-card">
                <div className="k-stat-icon" style={{ background: bg }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div>
                  <p className="k-stat-label">{label}</p>
                  <p className="k-stat-value" style={{ color }}>{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Insights */}
          {(summaryStats.topDoersOverdue?.length > 0 || summaryStats.topFMSOverdue?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="k-card p-4">
                <p className="k-section-title">Top Overdue Doers</p>
                <div className="space-y-1.5">
                  {summaryStats.topDoersOverdue?.slice(0, 4).map((d, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{d.name}</span>
                      <span className="k-badge-overdue">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="k-card p-4">
                <p className="k-section-title">Top Overdue FMS</p>
                <div className="space-y-1.5">
                  {summaryStats.topFMSOverdue?.slice(0, 4).map((f, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
                      <span className="text-sm truncate mr-2" style={{ color: 'var(--text-secondary)' }}>{f.name}</span>
                      <span className="k-badge-overdue flex-shrink-0">{f.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
          <div className={`k-card p-4 k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`}>
            <div className="k-filter-bar">
              <div className="k-search-wrap">
                <Search size={14} className="k-search-icon" />
                <input type="text" placeholder="Search tasks…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="k-search" />
              </div>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="k-select">
                <option value="fms">Group by FMS</option>
                <option value="doer">Group by Doer</option>
                <option value="all">Hierarchical View</option>
              </select>
              <button
                onClick={refresh}
                disabled={isRefreshing}
                title={lastRefresh ? `Refresh · Last updated ${new Date(lastRefresh).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Refresh data'}
                className="k-refresh-btn w-8 h-8 flex-shrink-0 px-0 justify-center"
                style={{ cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
              >
                <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Tasks */}
          {Object.keys(groupedTasks).length === 0 ? (
            <div className="k-card">
              <div className="k-empty">
                <div className="k-empty-icon"><FileText className="w-7 h-7" /></div>
                <p className="k-empty-title">No FMS Tasks Found</p>
                <p className="k-empty-msg">{tasks.length === 0 ? `No FMS tasks assigned to ${currentUser.name}` : 'No tasks match your current search criteria'}</p>
              </div>
            </div>
          ) : (
            <div>
              {groupBy === 'all' ? renderHierarchicalView(groupedTasks) : renderRegularGroupedView(groupedTasks)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PCTasks;
